import { useEffect, useMemo, useState, type ReactNode } from "react";
import { PhotoEdit } from "./PhotoEdit.tsx";
import { serviceLabel, type Picklists } from "@pagelink/shared";
import { api, ApiError, type Actor, type AdminPerson, type AdminRotaRow, type AdminShift } from "./api.ts";
import { Avatar, Banner, Btn, Icon, Note, Pill } from "./ui.tsx";

type Section = "people" | "rota" | "services" | "fees" | "shifts";
const SECTIONS: [Section, string][] = [["people", "People"], ["rota", "Rota"], ["services", "Services"], ["fees", "Fee codes"], ["shifts", "Shifts"]];
const SEP = "\u0001";

/* ---------------------------------------------------------------------------
 * Admin. The hospital's own data, kept by its admins: people and their
 * sign-in links, the rota, divisions and their services, fee codes and shift
 * hours. The server refuses all of it to anyone who is not an admin.
 * ------------------------------------------------------------------------ */
export function Admin({ me, onClose }: { me: Actor; onClose: () => void }) {
  const [section, setSection] = useState<Section>("people");
  const [lists, setLists] = useState<Picklists | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const reloadLists = () => api.picklists().then(setLists).catch(() => {});
  useEffect(() => { void reloadLists(); }, []);

  // Every write goes through here: one busy state, one error line, one "saved".
  const act = async (fn: () => Promise<unknown>, done?: string) => {
    setError(null);
    setSaved(null);
    try {
      await fn();
      if (done) setSaved(done);
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "That did not work.");
      return false;
    }
  };

  return (
    <>
      <button className="back" onClick={onClose}><Icon name="back" size={15} sw={2.2} />Back</button>
      <h1 className="screen-title">Admin</h1>
      <p className="screen-sub">VGH data for page-com. Changes apply straight away for everyone.</p>
      <select aria-label="Section" value={section} onChange={(e) => { setSection(e.target.value as Section); setError(null); setSaved(null); }} style={{ marginBottom: 12 }}>
        {SECTIONS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
      </select>
      {error && <Banner kind="err">{error}</Banner>}
      {saved && <Banner kind="info">{saved}</Banner>}
      {section === "people" && <People me={me} lists={lists} act={act} />}
      {section === "rota" && <Rota lists={lists} act={act} />}
      {section === "services" && <Services lists={lists} act={act} reload={reloadLists} />}
      {section === "fees" && <Fees lists={lists} act={act} reload={reloadLists} />}
      {section === "shifts" && <Shifts act={act} />}
    </>
  );
}

type Act = (fn: () => Promise<unknown>, done?: string) => Promise<boolean>;

function Field({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return <label className={`afield${wide ? " wide" : ""}`}><span>{label}</span>{children}</label>;
}

function DivisionOptions({ lists, withServices }: { lists: Picklists | null; withServices?: boolean }) {
  const groups = Array.from(new Set((lists?.services ?? []).map((s) => s.grp)));
  return (
    <>
      {groups.map((g) => (
        <optgroup key={g} label={g}>
          {(lists?.services ?? []).filter((s) => s.grp === g).flatMap((s) => [
            <option key={s.name} value={withServices ? `${s.name}${SEP}` : s.name}>{s.name}</option>,
            ...(withServices ? (lists?.subServices ?? []).filter((x) => x.division === s.name).map((x) => (
              <option key={`${s.name}${x.name}`} value={`${s.name}${SEP}${x.name}`}>{` ${serviceLabel(s.name, x.name, s.short)}`}</option>
            )) : []),
          ])}
        </optgroup>
      ))}
    </>
  );
}

/* ---- people ---- */

const BLANK_PERSON = { name: "", title: "Attending", service: "", isAdmin: false, email: "", phone: "", disabled: false };

function People({ me, lists, act }: { me: Actor; lists: Picklists | null; act: Act }) {
  const [people, setPeople] = useState<AdminPerson[] | null>(null);
  const [edit, setEdit] = useState<AdminPerson | "new" | null>(null);
  const [form, setForm] = useState(BLANK_PERSON);
  const [link, setLink] = useState<{ name: string; url: string } | null>(null);
  const [q, setQ] = useState("");
  const load = () => api.admin.people().then((r) => setPeople(r.people)).catch(() => {});
  useEffect(() => { void load(); }, []);

  const open = (p: AdminPerson | "new") => {
    setEdit(p);
    setLink(null);
    setForm(p === "new" ? BLANK_PERSON : { name: p.name, title: p.title, service: p.service, isAdmin: p.isAdmin, email: p.email ?? "", phone: p.phone ?? "", disabled: p.disabled });
  };
  const save = async () => {
    const id = edit === "new" ? null : edit!.id;
    if (await act(() => api.admin.savePerson(id, { ...form, email: form.email || null, phone: form.phone || null }), `${form.name} saved.`)) {
      setEdit(null);
      void load();
    }
  };
  const makeLink = async (p: AdminPerson) => {
    await act(async () => { const r = await api.admin.enrolLink(p.id); setLink({ name: p.name, url: r.link }); });
  };

  if (edit) {
    const self = edit !== "new" && edit.id === me.id;
    return (
      <div className="aform">
        <h2 className="asub">{edit === "new" ? "Add a person" : `Edit ${edit.name}`}</h2>
        {edit !== "new" && (
          <div style={{ gridColumn: "1 / -1" }}>
            <PhotoEdit id={edit.id} name={edit.name} size={56} onSave={(d) => api.setPhotoFor(edit.id, d)} onRemove={() => api.removePhotoFor(edit.id)} />
          </div>
        )}
        <Field label="Name" wide><input type="text" value={form.name} maxLength={60} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Dr. Jane Wong" /></Field>
        <Field label="Title"><input type="text" value={form.title} maxLength={40} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Field>
        <Field label="Home division">
          <select value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}>
            <option value="">Choose…</option>
            <DivisionOptions lists={lists} />
          </select>
        </Field>
        <Field label="Email" wide><input type="email" value={form.email} maxLength={120} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@vch.ca" /></Field>
        <Field label="Primary phone" wide><input type="tel" className="mono" value={form.phone} maxLength={16} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^0-9+]/g, "") })} /></Field>
        <label className="acheck"><input type="checkbox" checked={form.isAdmin} disabled={self} onChange={(e) => setForm({ ...form, isAdmin: e.target.checked })} /><span>Admin: can change everything on this screen</span></label>
        {edit !== "new" && <label className="acheck"><input type="checkbox" checked={form.disabled} disabled={self} onChange={(e) => setForm({ ...form, disabled: e.target.checked })} /><span>Disabled: cannot sign in or be paged; their pages are kept</span></label>}
        <div className="row" style={{ marginTop: 10 }}>
          <Btn full soft tone="dim" onClick={() => setEdit(null)}>Cancel</Btn>
          <Btn full disabled={form.name.trim().length < 2 || !form.service} onClick={() => void save()}>Save</Btn>
        </div>
      </div>
    );
  }

  const shown = (people ?? []).filter((p) => !q.trim() || `${p.name} ${p.service} ${p.email ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <>
      <Btn full soft onClick={() => open("new")} style={{ marginBottom: 10 }}><Icon name="plus" size={15} sw={2.4} />Add a person</Btn>
      {link && (
        <div className="alink">
          <div>Sign-in link for <b>{link.name}</b>, single use, valid 7 days. Send it to them; opening it on their phone creates their passkey.</div>
          <input type="text" readOnly value={link.url} onFocus={(e) => e.target.select()} className="mono" />
          <Btn sm onClick={() => void navigator.clipboard?.writeText(link.url)}>Copy link</Btn>
        </div>
      )}
      <div className="search">
        <span className="ic"><Icon name="search" size={16} /></span>
        <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a person" aria-label="Find a person" />
      </div>
      {shown.map((p) => (
        <div key={p.id} className={`arow${p.disabled ? " off" : ""}`}>
          <Avatar id={p.id} name={p.name} size={30} fontSize={13} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="nm">{p.name} {p.isAdmin && <Pill tone="blue">Admin</Pill>} {p.disabled && <Pill tone="stat">Disabled</Pill>}</div>
            <div className="tt">{p.title} · {p.service} · {p.upn ? "Teams" : p.hasPasskey ? "Passkey" : "Not signed in yet"}</div>
          </div>
          {!p.disabled && <button className="linkish" onClick={() => void makeLink(p)}>Link</button>}
          <button className="linkish" onClick={() => open(p)}>Edit</button>
        </div>
      ))}
    </>
  );
}

/* ---- rota ---- */

const today = () => new Date().toLocaleDateString("en-CA");

function Rota({ lists, act }: { lists: Picklists | null; act: Act }) {
  const [rows, setRows] = useState<AdminRotaRow[] | null>(null);
  const [people, setPeople] = useState<AdminPerson[]>([]);
  const [shifts, setShifts] = useState<AdminShift[]>([]);
  const [from, setFrom] = useState(today());
  const [f, setF] = useState({ target: "", clinicianId: "", shiftId: "day", position: 1, startDate: today(), days: 7 });

  const load = () => {
    const a = new Date(`${from}T00:00`), b = new Date(a); b.setDate(b.getDate() + 7);
    api.admin.rota(a, b).then((r) => setRows(r.rows)).catch(() => setRows([]));
  };
  useEffect(load, [from]);
  useEffect(() => {
    api.admin.people().then((r) => setPeople(r.people.filter((p) => !p.disabled))).catch(() => {});
    api.admin.shifts().then((r) => setShifts(r.shifts)).catch(() => {});
  }, []);

  const add = async () => {
    const [service = "", sub = ""] = f.target.split(SEP);
    const who = people.find((p) => p.id === f.clinicianId)?.name ?? "";
    if (await act(() => api.admin.addRota({ service, subService: sub || null, clinicianId: f.clinicianId, position: f.position, shiftId: f.shiftId, startDate: f.startDate, days: f.days }),
      `${who} added for ${f.days} day${f.days === 1 ? "" : "s"}.`)) load();
  };

  const byDay = useMemo(() => {
    const m = new Map<string, AdminRotaRow[]>();
    for (const r of rows ?? []) {
      const k = new Date(r.startsAt).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()];
  }, [rows]);
  const hm = (t: string) => new Date(t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const long = (r: AdminRotaRow) => Date.parse(r.endsAt) - Date.parse(r.startsAt) > 86400_000;

  return (
    <>
      <div className="aform">
        <h2 className="asub">Add shifts</h2>
        <Field label="Division or service" wide>
          <select value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })}>
            <option value="">Choose…</option>
            <DivisionOptions lists={lists} withServices />
          </select>
        </Field>
        <Field label="Person" wide>
          <select value={f.clinicianId} onChange={(e) => setF({ ...f, clinicianId: e.target.value })}>
            <option value="">Choose…</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.service}</option>)}
          </select>
        </Field>
        <Field label="Shift">
          <select value={f.shiftId} onChange={(e) => setF({ ...f, shiftId: e.target.value })}>
            {shifts.map((s) => <option key={s.id} value={s.id}>{s.label} · {s.starts}–{s.ends}</option>)}
          </select>
        </Field>
        <Field label="Call order">
          <select value={f.position} onChange={(e) => setF({ ...f, position: Number(e.target.value) })}>
            <option value={1}>1st call</option>
            <option value={2}>Backup (2nd)</option>
            <option value={3}>3rd</option>
          </select>
        </Field>
        <Field label="First day"><input type="date" value={f.startDate} onChange={(e) => setF({ ...f, startDate: e.target.value })} /></Field>
        <Field label="For how many days">
          <input type="text" inputMode="numeric" value={f.days} onChange={(e) => setF({ ...f, days: Math.max(1, Math.min(62, Number(e.target.value.replace(/\D/g, "")) || 1)) })} />
        </Field>
        <Btn full disabled={!f.target || !f.clinicianId} onClick={() => void add()} style={{ marginTop: 6 }}>
          <Icon name="plus" size={14} sw={2.4} />Add {f.days} shift{f.days === 1 ? "" : "s"}
        </Btn>
      </div>

      <div className="filterbar" style={{ marginTop: 14 }}>
        <span className="asub" style={{ margin: 0, flex: 1 }}>Week from</span>
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: 170 }} />
      </div>
      {rows && rows.length === 0 && <Note>No shifts in this week.</Note>}
      {byDay.map(([day, list]) => (
        <div key={day} className="aday">
          <div className="aday-h">{day}</div>
          {list.map((r) => (
            <div key={r.id} className="arow tight">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="nm">{r.name} <span className="tt">· {r.position === 1 ? "1st call" : r.position === 2 ? "backup" : `${r.position}rd`}</span></div>
                <div className="tt">{serviceLabel(r.service, r.subService)} · {r.shift ?? (long(r) ? "continuous" : "custom")} {long(r) ? `until ${new Date(r.endsAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : `${hm(r.startsAt)}–${hm(r.endsAt)}`}</div>
              </div>
              <button className="linkish danger" onClick={() => void act(() => api.admin.removeRota(r.id), "Shift removed.").then((ok) => ok && load())}>Remove</button>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}

/* ---- divisions and services ---- */

function Services({ lists, act, reload }: { lists: Picklists | null; act: Act; reload: () => Promise<unknown> }) {
  const [div, setDiv] = useState({ name: "", grp: "Medicine", short: "" });
  const [svc, setSvc] = useState({ division: "", name: "" });
  const groups = Array.from(new Set((lists?.services ?? []).map((s) => s.grp)));
  const then = (ok: boolean) => { if (ok) void reload(); };

  return (
    <>
      <div className="aform">
        <h2 className="asub">Add a service to a division</h2>
        <Field label="Division" wide>
          <select value={svc.division} onChange={(e) => setSvc({ ...svc, division: e.target.value })}>
            <option value="">Choose…</option>
            <DivisionOptions lists={lists} />
          </select>
        </Field>
        <Field label="Service name" wide><input type="text" value={svc.name} maxLength={40} onChange={(e) => setSvc({ ...svc, name: e.target.value })} placeholder="OPAT" /></Field>
        <Btn full disabled={!svc.division || !svc.name.trim()} style={{ marginTop: 6 }}
          onClick={() => void act(() => api.admin.saveService(svc.division, svc.name.trim()), `${svc.name.trim()} added to ${svc.division}.`).then((ok) => { then(ok); if (ok) setSvc({ ...svc, name: "" }); })}>
          Add service
        </Btn>
      </div>

      <div className="aform">
        <h2 className="asub">Add or change a division</h2>
        <Field label="Name" wide><input type="text" value={div.name} maxLength={40} onChange={(e) => setDiv({ ...div, name: e.target.value })} placeholder="Infectious Diseases" /></Field>
        <Field label="Group">
          <input type="text" list="admin-groups" value={div.grp} maxLength={30} onChange={(e) => setDiv({ ...div, grp: e.target.value })} />
          <datalist id="admin-groups">{groups.map((g) => <option key={g} value={g} />)}</datalist>
        </Field>
        <Field label="Short name"><input type="text" value={div.short} maxLength={12} onChange={(e) => setDiv({ ...div, short: e.target.value })} placeholder="ID" /></Field>
        <Btn full disabled={div.name.trim().length < 2 || div.grp.trim().length < 2} style={{ marginTop: 6 }}
          onClick={() => void act(() => api.admin.saveDivision(div), `${div.name} saved.`).then(then)}>Save division</Btn>
      </div>

      {groups.map((g) => (
        <div key={g} className="aday">
          <div className="aday-h">{g}</div>
          {(lists?.services ?? []).filter((s) => s.grp === g).map((s) => {
            const subs = (lists?.subServices ?? []).filter((x) => x.division === s.name);
            return (
              <div key={s.name} className="arow tight" style={{ alignItems: "flex-start" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="nm">{s.name}{s.short ? <span className="tt"> · {s.short}</span> : null}</div>
                  {subs.length > 0 && (
                    <div className="team-members" style={{ marginTop: 4 }}>
                      {subs.map((x) => (
                        <span key={x.name} className="team-chip" style={{ paddingLeft: 9 }}>
                          {x.name}
                          <button className="chip-x" aria-label={`Retire ${x.name}`}
                            onClick={() => window.confirm(`Retire ${serviceLabel(s.name, x.name, s.short)}? Old pages keep the name.`) && void act(() => api.admin.saveService(s.name, x.name, true), `${x.name} retired.`).then(then)}>
                            <Icon name="x" size={11} sw={2.6} />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button className="linkish danger" onClick={() => window.confirm(`Retire ${s.name}? It disappears from the lists; old pages keep the name.`) && void act(() => api.admin.retireDivision(s.name), `${s.name} retired.`).then(then)}>Retire</button>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

/* ---- fee codes ---- */

function Fees({ lists, act, reload }: { lists: Picklists | null; act: Act; reload: () => Promise<unknown> }) {
  const [f, setF] = useState({ code: "", label: "" });
  const then = (ok: boolean) => { if (ok) void reload(); };
  return (
    <>
      <div className="aform">
        <h2 className="asub">Add or change a fee code</h2>
        <Field label="Code"><input type="text" className="mono" value={f.code} maxLength={12} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="00000" /></Field>
        <Field label="Description" wide><input type="text" value={f.label} maxLength={80} onChange={(e) => setF({ ...f, label: e.target.value })} placeholder="Telephone advice, specialist" /></Field>
        <Btn full disabled={!f.code.trim() || !f.label.trim()} style={{ marginTop: 6 }}
          onClick={() => void act(() => api.admin.saveFeeCode(f.code.trim(), f.label.trim()), `${f.code} saved.`).then((ok) => { then(ok); if (ok) setF({ code: "", label: "" }); })}>Save fee code</Btn>
        <div className="fine" style={{ textAlign: "left", marginTop: 6 }}>Saving an existing code changes its description. Pages already billed keep their code.</div>
      </div>
      {(lists?.feeCodes ?? []).map((c) => (
        <div key={c.code} className="arow tight">
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="nm mono">{c.code}</div>
            <div className="tt">{c.label}</div>
          </div>
          <button className="linkish" onClick={() => setF({ code: c.code, label: c.label })}>Edit</button>
          <button className="linkish danger" onClick={() => window.confirm(`Retire ${c.code}?`) && void act(() => api.admin.saveFeeCode(c.code, c.label, true), `${c.code} retired.`).then(then)}>Retire</button>
        </div>
      ))}
    </>
  );
}

/* ---- shift hours ---- */

function Shifts({ act }: { act: Act }) {
  const [shifts, setShifts] = useState<AdminShift[]>([]);
  useEffect(() => { api.admin.shifts().then((r) => setShifts(r.shifts)).catch(() => {}); }, []);
  const set = (id: string, k: keyof AdminShift, v: string) => setShifts((xs) => xs.map((s) => (s.id === id ? { ...s, [k]: v } : s)));
  return (
    <>
      <Note>These hours label the shifts and fill in new rota entries. Shifts already on the rota keep the times they were added with.</Note>
      {shifts.map((s) => (
        <div key={s.id} className="aform">
          <Field label="Name" wide><input type="text" value={s.label} maxLength={30} onChange={(e) => set(s.id, "label", e.target.value)} /></Field>
          <Field label="Starts"><input type="text" className="mono" value={s.starts} maxLength={5} onChange={(e) => set(s.id, "starts", e.target.value)} placeholder="08:00" /></Field>
          <Field label="Ends"><input type="text" className="mono" value={s.ends} maxLength={5} onChange={(e) => set(s.id, "ends", e.target.value)} placeholder="17:00" /></Field>
          <Btn full sm soft style={{ marginTop: 6 }} onClick={() => void act(() => api.admin.saveShift(s.id, s), `${s.label} saved.`)}>Save {s.label}</Btn>
        </div>
      ))}
    </>
  );
}
